import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Box, Button, Divider, Stack, TextField, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import moment from 'moment';
import type AdminCompaniesStore from '../../store/adminCompanies/adminCompaniesStore';
import { groupOrganisationNumber } from '../../store/purchase/organisationNumber';
import CircularLoading from '../../components/loading/circularLoading';
import useConfirm from '../../hooks/useConfirm';
import { companyStatusLabel, companyStatusOf } from './companyStatusLabels';
import AdminCompanyMembers from './AdminCompanyMembers';

/**
 * The company page, at `/admin/companies/{id}`: one company's name, grouped organisation number,
 * created and status; its invoice email to change; a refresh of its name from the register; Close or
 * Reopen, and Delete while nothing has ever pointed at it; and its members. Close and Delete are
 * confirmed first. A refusal shows as the backend sent it.
 *
 * A thin observer over the admin companies store.
 */
const AdminCompanyPage = observer(function AdminCompanyPage({ adminCompaniesStore, companyId, onLeave }: { adminCompaniesStore: AdminCompaniesStore; companyId: number; onLeave: () => void }) {
  const { company, companyError, isLoadingCompany, isWorking, actionError, nameRefresh, invoiceEmailDraft, canSaveInvoiceEmail } = adminCompaniesStore;
  const name = company?.name ?? '';

  const [CloseConfirmation, confirmClose] = useConfirm(
    `Close ${name}?`,
    'Its members will be removed from the company. No one can buy credits for it until it is reopened.',
    true,
    'Close company',
  );
  const [DeleteConfirmation, confirmDelete] = useConfirm(`Delete ${name}?`, 'The company is removed for good. This cannot be undone.', true, 'Delete company');

  useEffect(() => {
    void adminCompaniesStore.openCompany(companyId);
  }, [adminCompaniesStore, companyId]);

  const back = (
    <Button variant='text' startIcon={<ArrowBackIcon />} onClick={onLeave} sx={{ alignSelf: 'flex-start' }}>
      Back to companies
    </Button>
  );

  if (isLoadingCompany) {
    return <CircularLoading loadingText='Loading the company...' />;
  }

  if (company === null) {
    return (
      <Stack spacing={2} sx={{ maxWidth: 800, mx: 'auto' }}>
        {back}
        {companyError !== null && (
          <Typography variant='body2' color='error' role='alert'>
            {companyError.message}
          </Typography>
        )}
      </Stack>
    );
  }

  const isClosed = companyStatusOf(company) === 'closed';

  const close = async () => {
    if (await confirmClose()) await adminCompaniesStore.closeCompany();
  };

  const remove = async () => {
    if (!(await confirmDelete())) return;
    if (await adminCompaniesStore.deleteCompany()) onLeave();
  };

  return (
    <Stack spacing={3} sx={{ maxWidth: 800, mx: 'auto' }}>
      {back}

      <Stack spacing={0.5}>
        <Typography variant='h5'>{company.name}</Typography>
        <Typography variant='body2' color='text.secondary'>
          Organisation number {groupOrganisationNumber(company.orgNumber)}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Created {moment(company.createdAtUtc).format('DD.MM.YYYY HH:mm')}
        </Typography>
        <Typography variant='body2' color={isClosed ? 'text.disabled' : 'text.secondary'} data-testid='company-status'>
          Status: {companyStatusLabel(company)}
          {isClosed && ` (${moment(company.closedAtUtc).format('DD.MM.YYYY HH:mm')})`}
        </Typography>
      </Stack>

      <Box component='form' sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }} onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        void adminCompaniesStore.saveInvoiceEmail();
      }}>
        <TextField
          label='Invoice email'
          type='email'
          value={invoiceEmailDraft}
          onChange={(event) => adminCompaniesStore.setInvoiceEmailDraft(event.target.value)}
          size='small'
          sx={{ flex: 1 }}
          disabled={isWorking}
        />
        <Button type='submit' variant='contained' disabled={!canSaveInvoiceEmail}>
          Save
        </Button>
      </Box>

      <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap'>
        <Button variant='outlined' onClick={() => void adminCompaniesStore.refreshName()} disabled={isWorking}>
          Refresh name from the register
        </Button>
        {nameRefresh !== null && (
          <Typography variant='body2' data-testid='name-refresh'>
            {nameRefresh === 'changed' ? company.name : 'uendret'}
          </Typography>
        )}
      </Stack>

      <Stack direction='row' spacing={2}>
        {isClosed ? (
          <Button variant='outlined' onClick={() => void adminCompaniesStore.reopenCompany()} disabled={isWorking}>
            Reopen company
          </Button>
        ) : (
          <Button variant='outlined' color='error' onClick={() => void close()} disabled={isWorking}>
            Close company
          </Button>
        )}
        {company.mayBeDeleted && (
          <Button variant='outlined' color='error' onClick={() => void remove()} disabled={isWorking}>
            Delete company
          </Button>
        )}
      </Stack>

      {actionError !== null && (
        <Typography variant='body2' color='error' role='alert'>
          {actionError.message}
        </Typography>
      )}

      <Divider />

      <AdminCompanyMembers adminCompaniesStore={adminCompaniesStore} />

      <CloseConfirmation />
      <DeleteConfirmation />
    </Stack>
  );
});

export default AdminCompanyPage;
